/* Elysium Relief — behaviour for the captured GoDaddy markup.
 *
 * The old site was a React app. The capture kept the rendered HTML and threw
 * away every event handler with it, so three controls are inert on the live
 * store:
 *
 *   1. The mobile menu button. It had no handler, and the panel it opened was
 *      rendered conditionally by React, so it was never in the captured HTML
 *      at all — there is nothing on the page for it to drop down.
 *   2. The cookie banner's Accept and Decline buttons. Neither did anything,
 *      and the banner had no dismissed state, so it sat over the bottom of
 *      every phone screen permanently with no way to close it.
 *   3. The six FAQ chevrons on the product page. The answers are in the HTML
 *      and all six sit open, so nothing is hidden — the buttons just do nothing.
 *
 * Everything below degrades to the current behaviour if this file fails to
 * load: the nav panel simply never appears, the banner stays visible, and the
 * FAQ stays expanded. Nothing that is readable today is hidden by CSS that
 * only JavaScript can undo.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 768px)';
  var CONSENT_KEY = 'er-cookie-consent';

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ------------------------------------------------------------------ *
   * 1. Mobile navigation                                                *
   * ------------------------------------------------------------------ */

  function mobileNav() {
    var header = document.querySelector('header');
    if (!header) return;

    var toggle = header.querySelector('button[aria-label="Toggle menu"]');
    var nav = header.querySelector('nav');
    if (!toggle || !nav || toggle.getAttribute('data-er-wired')) return;
    toggle.setAttribute('data-er-wired', '1');

    // The desktop nav is in the markup on every page, just hidden under 768px.
    // Cloning it keeps one source of truth for the links instead of a second
    // copy that can drift out of step.
    var panel = document.createElement('div');
    panel.className = 'er-mnav';
    panel.id = 'er-mnav';
    panel.hidden = true;

    var list = document.createElement('ul');
    list.className = 'er-mnav__list';

    each(nav.querySelectorAll('a'), function (source) {
      var item = document.createElement('li');
      var link = source.cloneNode(true);
      // Drop the captured Tailwind classes — they are tuned for a horizontal
      // bar and fight the stacked panel.
      link.className = /bg-primary/.test(source.className)
        ? 'er-mnav__cta'
        : 'er-mnav__link';
      item.appendChild(link);
      list.appendChild(item);
    });

    if (!list.children.length) return;
    panel.appendChild(list);
    header.appendChild(panel);

    // The captured button holds a hamburger icon only. Add a close icon so the
    // control reads as a toggle rather than a one-way door.
    var close = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    close.setAttribute('class', 'er-mnav__close-icon');
    close.setAttribute('width', '22');
    close.setAttribute('height', '22');
    close.setAttribute('viewBox', '0 0 24 24');
    close.setAttribute('fill', 'none');
    close.setAttribute('stroke', 'currentColor');
    close.setAttribute('stroke-width', '2');
    close.setAttribute('stroke-linecap', 'round');
    close.setAttribute('aria-hidden', 'true');
    close.innerHTML = '<line x1="18" y1="6" x2="6" y2="18"></line>' +
                      '<line x1="6" y1="6" x2="18" y2="18"></line>';
    toggle.appendChild(close);
    toggle.classList.add('er-mnav__toggle');

    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'er-mnav');

    function open() {
      panel.hidden = false;
      header.classList.add('er-nav-open');
      document.documentElement.classList.add('er-noscroll');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      var first = panel.querySelector('a');
      if (first) first.focus();
    }

    function shut(returnFocus) {
      panel.hidden = true;
      header.classList.remove('er-nav-open');
      document.documentElement.classList.remove('er-noscroll');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Toggle menu');
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (panel.hidden) open();
      else shut(true);
    });

    // A link to an on-page anchor does not reload, so the panel has to be told
    // to close itself.
    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) shut(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) shut(true);
    });

    // Rotating to landscape can cross the breakpoint with the panel open,
    // which would leave the page scroll-locked behind the desktop nav.
    var wide = window.matchMedia(DESKTOP);
    var onChange = function (e) { if (e.matches && !panel.hidden) shut(false); };
    if (wide.addEventListener) wide.addEventListener('change', onChange);
    else if (wide.addListener) wide.addListener(onChange);
  }

  /* ------------------------------------------------------------------ *
   * 2. Cookie consent                                                   *
   * ------------------------------------------------------------------ */

  function readConsent() {
    try { return window.localStorage.getItem(CONSENT_KEY); }
    catch (e) { return null; }
  }

  function writeConsent(value) {
    try { window.localStorage.setItem(CONSENT_KEY, value); }
    catch (e) { /* private browsing — the banner reappears next visit */ }
  }

  // Tell Shopify about the choice, so Accept and Decline actually govern the
  // analytics and marketing scripts rather than only hiding the bar.
  function applyConsent(granted) {
    updateGoogleConsent(granted);
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) {
        if (error) return;
        var privacy = window.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.setTrackingConsent !== 'function') return;
        privacy.setTrackingConsent({
          analytics: granted,
          marketing: granted,
          preferences: granted,
          sale_of_data: granted
        }, function () {});
      }
    );
  }

  // Google Consent Mode v2. The tag in oldsite-analytics.liquid starts denied
  // (or granted, for someone who already accepted on an earlier visit); this
  // is the only thing that ever changes it. Without this, Decline would hide
  // the bar and carry on tracking.
  function updateGoogleConsent(granted) {
    if (typeof window.gtag !== 'function') return;
    var state = granted ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
      functionality_storage: state,
      personalization_storage: state
    });
  }

  function cookieBanner() {
    var banner = document.querySelector('[aria-label="Cookie consent banner"]');
    if (!banner) return;

    if (readConsent()) {
      banner.parentNode.removeChild(banner);
      return;
    }

    var accept = null;
    var decline = null;
    each(banner.querySelectorAll('button'), function (btn) {
      var label = (btn.textContent || '').trim().toLowerCase();
      if (label === 'accept') accept = btn;
      if (label === 'decline') decline = btn;
    });
    if (!accept && !decline) return;

    function answer(value, granted) {
      writeConsent(value);
      applyConsent(granted);
      banner.parentNode.removeChild(banner);
    }

    if (accept) {
      accept.setAttribute('type', 'button');
      accept.addEventListener('click', function () { answer('accepted', true); });
    }
    if (decline) {
      decline.setAttribute('type', 'button');
      decline.addEventListener('click', function () { answer('declined', false); });
    }
  }

  /* ------------------------------------------------------------------ *
   * 3. FAQ accordion                                                    *
   * ------------------------------------------------------------------ */

  function accordion() {
    var buttons = document.querySelectorAll('button:not([data-er-wired])');
    var index = 0;

    each(buttons, function (btn) {
      if (!btn.querySelector('.lucide-chevron-down')) return;

      var panel = btn.nextElementSibling;
      if (!panel || panel.className.indexOf('overflow-hidden') === -1) return;

      btn.setAttribute('data-er-wired', '1');
      btn.setAttribute('type', 'button');

      var id = 'er-faq-' + (++index);
      panel.id = id;
      panel.classList.add('er-acc__panel');
      btn.classList.add('er-acc__trigger');
      btn.setAttribute('aria-controls', id);

      // Collapsed state is added here rather than sitting in the stylesheet, so
      // that if this script never runs the answers stay readable.
      function set(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.classList.toggle('er-acc__panel--shut', !open);
      }

      set(index === 1);

      btn.addEventListener('click', function () {
        set(btn.getAttribute('aria-expanded') !== 'true');
      });
    });
  }

  // Discount codes on the basket page.
  //
  // Shopify gives Liquid no way to apply a code and no cart form field that
  // does it either. The only route from the storefront is a GET to
  // /discount/<code>, which stores the code against the session and then
  // redirects wherever you point it. Sending them back to /cart rather than
  // straight to checkout is deliberate — the saving lands in front of them
  // before they are asked to pay, which is the whole point of the box.
  function discountForm() {
    var form = document.querySelector('[data-er-discount]');
    if (!form) return;

    var input = form.querySelector('input[name="discount"]');
    var msg = form.querySelector('.er-cart-discount__msg');
    if (!input) return;

    function say(text) {
      if (!msg) return;
      msg.textContent = text;
      msg.hidden = !text;
    }

    // Remembering the attempt across the redirect has to be sessionStorage, not
    // a query parameter: Shopify rewrites the ?redirect= target and throws any
    // query string on it away, so ?tried= never arrives. Verified against the
    // live shop — every variant of the encoding came back as a bare /cart.
    var KEY = 'er-discount-tried';

    function remember(code) {
      try { window.sessionStorage.setItem(KEY, code); } catch (e) { /* private mode */ }
    }

    function recall() {
      try {
        var v = window.sessionStorage.getItem(KEY);
        window.sessionStorage.removeItem(KEY);
        return v;
      } catch (e) {
        return null;
      }
    }

    // Coming back from /discount/<code>. Shopify accepts a bad code silently:
    // no error, no discount, nothing on the page changes. The codes that did
    // land are listed on the form by Liquid, so a code that is missing from
    // that list was rejected — say so rather than leaving them staring at an
    // unchanged total.
    var tried = recall();
    if (tried) {
      var landed = (form.getAttribute('data-er-applied') || '').toUpperCase();
      if (landed.indexOf(tried.toUpperCase() + '|') !== -1) {
        say('');
      } else {
        say('That code was not recognised. Check it and try again.');
        input.value = tried;
        input.setAttribute('aria-invalid', 'true');
      }
    }

    form.addEventListener('submit', function (event) {
      var code = (input.value || '').trim();

      // Always our handler, never the plain GET — the fallback action only
      // exists for the no-JS case, and an empty box must not fling someone
      // into checkout.
      event.preventDefault();

      if (!code) {
        say('Enter your code first.');
        input.focus();
        return;
      }

      input.removeAttribute('aria-invalid');
      remember(code);
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
    });
  }

  // Promotion bar dismissal.
  //
  // The bar itself is plain HTML and CSS and shows without any of this. The
  // only job here is remembering that someone closed it, keyed to the discount
  // code so that a new offer reappears for everyone rather than staying hidden
  // for anyone who dismissed the last one. Hiding on a repeat visit is done by
  // the inline script in oldsite-promo-bar.liquid, before first paint, so the
  // page never renders the bar and then collapses it.
  function promoBar() {
    var bar = document.querySelector('[data-er-promo]');
    if (!bar) return;

    var close = bar.querySelector('[data-er-promo-close]');
    if (!close) return;

    close.addEventListener('click', function () {
      try {
        window.localStorage.setItem('er-promo-dismissed', bar.getAttribute('data-er-promo-key') || '1');
      } catch (e) { /* private mode — it will be back next page load */ }
      // The class carries --er-banner-h: 0, so the header and the page content
      // move up with the bar instead of leaving a gap where it was.
      document.documentElement.classList.add('er-promo-off');
    });
  }

  function init() {
    mobileNav();
    cookieBanner();
    accordion();
    discountForm();
    promoBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
